import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Plus, ArrowDownUp, AlertTriangle, ShoppingCart, Download, Upload, FileSpreadsheet, Search } from "lucide-react";
import { toast } from "sonner";

// Production Flow's "Inventory" page — every store's stock in one place
// (RM Store through Finished Goods, not just the 9 job stages), folded into
// Production Flow so stock and production live under one module instead of
// a separate top-level "Inventory" page. Logic is unchanged from the
// standalone page it replaces; only the surrounding chrome (back-link,
// eyebrow) changed to match its new home.
//
// Column labels below (PART NUMBER / DESCRIPTION, and the Type column's
// values) intentionally mirror the vocabulary used in the stock-snapshot
// spreadsheets stores upload here, so a row in this table reads the same
// as a row in that sheet. Only these four item types have a known
// spreadsheet-style label; anything else (PWD, IMP, FAB, ASM, TEST, FI,
// RWK) falls back to its raw type code.
const TYPE_DISPLAY_LABELS = { RAW: "R/M", MS: "MACHINE SHOP", SFG: "SFG", FG: "FG" };

export default function ProductionFlowInventory() {
  const [inv, setInv] = useState([]);
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState("ALL");
  // Real-time search by SKU code or name, layered on top of the store filter
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [form, setForm] = useState({ item_id: "", warehouse: "RM_STORE", qty: 0, reorder_point: 0 });
  const [move, setMove] = useState({ item_id: "", warehouse: "RM_STORE", delta: 0, reason: "manual" });
  const [busy, setBusy] = useState(false);
  // Summary of the last stock import (parts updated / created / errors).
  const [importSummary, setImportSummary] = useState(null);
  const navigate = useNavigate();

  const autoPR = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/purchase-requests/auto-low-stock");
      if (data.purchase_request) {
        toast.success(`${data.purchase_request.code} created — ${data.purchase_request.lines.length} line(s)`);
        navigate("/purchase-requests");
      } else {
        toast.info(data.message);
      }
    } catch {
      toast.error("Auto-PR failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  // Export = simplified workbook (.xlsx) mirroring this table — Part Number,
  // Description, Type, Warehouse, On Hand, Reorder Point, Status; one row per
  // part per warehouse — scoped to the store tile currently selected.
  const exportInventory = async () => {
    try {
      const params = storeFilter === "ALL" ? {} : { warehouse: storeFilter };
      const { data } = await api.get("/inventory/export-xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      const today = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
      a.href = url;
      a.download = `inventory${storeFilter === "ALL" ? "" : `_${storeFilter}`}_${today}.xlsx`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/inventory/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // The imported file is a physical stock count: On Hand is REPLACED by the
      // file's qty (Fabrication / Powder Coating are never touched).
      setImportSummary({ file: file.name, ...data });
      const msg = `Physical stock updated from ${file.name} · ${data.updated} replaced, ${data.created} created` +
        (data.errors?.length ? ` · ${data.errors.length} error(s)` : "");
      if (data.errors?.length) toast.warning(msg);
      else toast.success(msg);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      toast.error(
        detail != null ? formatApiError(detail)
          : status === 413 ? "Import failed: the file is too large for the server to accept."
          : status ? `Import failed (server error ${status}). Please try again.`
          : "Import failed: could not reach the server.",
      );
    }
  };

  const load = async () => {
    const [i, it, s] = await Promise.all([api.get("/inventory"), api.get("/items"), api.get("/stores")]);
    setInv(i.data);
    setItems(it.data);
    setStores(s.data);
    if (!form.item_id && it.data.length) {
      setForm((f) => ({ ...f, item_id: it.data[0].id }));
      setMove((m) => ({ ...m, item_id: it.data[0].id }));
    }
  };

  useEffect(() => { load(); }, []);

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));
  const storeMap = Object.fromEntries(stores.map((s) => [s.code, s.name]));

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/inventory", form);
      toast.success("Inventory updated");
      setOpen(false);
      load();
    } finally {
      setBusy(false);
    }
  };

  const submitMove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/inventory/move", move);
      toast.success("Movement recorded");
      setMoveOpen(false);
      load();
    } finally {
      setBusy(false);
    }
  };

  const storeFiltered = storeFilter === "ALL" ? inv : inv.filter((r) => r.warehouse === storeFilter);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? storeFiltered.filter((r) => {
        const item = itemMap[r.item_id];
        return (item?.code || "").toLowerCase().includes(q) || (item?.name || "").toLowerCase().includes(q);
      })
    : storeFiltered;
  const sorted = [...filtered].sort((a, b) => {
    const ca = itemMap[a.item_id]?.code || "";
    const cb = itemMap[b.item_id]?.code || "";
    return ca.localeCompare(cb);
  });

  const storeTotals = stores.map((s) => {
    const rows = inv.filter((r) => r.warehouse === s.code);
    return {
      code: s.code,
      name: s.name,
      count: rows.length,
      low: rows.filter((r) => r.qty <= r.reorder_point).length,
    };
  });

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <Link to="/production-flow" className="text-xs text-blue-600 hover:underline mb-1 inline-flex items-center gap-1" data-testid="back-to-all-stages">
            ← All Stages
          </Link>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Production Flow</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }} data-testid="production-flow-heading">
            Inventory
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every store's stock in one place — Raw Material through Finished Goods, not just the production stages.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadCsv("/inventory/template-csv", "inventory_template.csv")} data-testid="inv-csv-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" onClick={exportInventory} title="Download inventory as Excel (.xlsx)" data-testid="inv-csv-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <label className="inline-flex items-center h-10 rounded-md border border-slate-200 px-3 text-sm cursor-pointer hover:bg-slate-50">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV / Excel
            <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={onImport} data-testid="inv-csv-import-input" />
          </label>
          <Button variant="outline" onClick={autoPR} disabled={busy} data-testid="inv-auto-pr-btn">
            <ShoppingCart className="w-4 h-4 mr-1.5" /> Auto-PR Low Stock
          </Button>
          <Sheet open={moveOpen} onOpenChange={setMoveOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" data-testid="move-btn"><ArrowDownUp className="w-4 h-4 mr-1.5" />Record Movement</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Stock Movement</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-4">
                <div>
                  <Label>Item</Label>
                  <Select value={move.item_id} onValueChange={(v) => setMove({ ...move, item_id: v })}>
                    <SelectTrigger data-testid="move-item-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Warehouse / Store</Label>
                  <Select value={move.warehouse} onValueChange={(v) => setMove({ ...move, warehouse: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {stores.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Delta (positive = receive, negative = issue)</Label>
                  <Input type="number" value={move.delta} onChange={(e) => setMove({ ...move, delta: parseFloat(e.target.value || 0) })} data-testid="move-delta-input" />
                </div>
                <div><Label>Reason</Label><Input value={move.reason} onChange={(e) => setMove({ ...move, reason: e.target.value })} /></div>
              </div>
              <SheetFooter className="mt-6"><Button onClick={submitMove} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-move-btn">Submit</Button></SheetFooter>
            </SheetContent>
          </Sheet>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-inv-btn"><Plus className="w-4 h-4 mr-1.5" />Set Stock</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Set Inventory</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-4">
                <div>
                  <Label>Item</Label>
                  <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                    <SelectTrigger data-testid="inv-item-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Warehouse / Store</Label>
                  <Select value={form.warehouse} onValueChange={(v) => setForm({ ...form, warehouse: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {stores.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Quantity</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: parseFloat(e.target.value || 0) })} /></div>
                <div><Label>Reorder Point</Label><Input type="number" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: parseFloat(e.target.value || 0) })} /></div>
              </div>
              <SheetFooter className="mt-6"><Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-inv-btn">Save</Button></SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="store-tiles">
        <button
          onClick={() => setStoreFilter("ALL")}
          className={`text-left bg-white border rounded-lg p-3 hover:border-blue-300 transition ${storeFilter === "ALL" ? "border-blue-600 ring-1 ring-blue-100" : "border-slate-200"}`}
          data-testid="store-tile-ALL"
        >
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">All Stores</div>
          <div className="mt-1 text-xl font-extrabold text-slate-900 mono">{inv.length}</div>
          <div className="text-[11px] text-slate-500">SKU lines</div>
        </button>
        {storeTotals.map((s) => (
          <button
            key={s.code}
            onClick={() => setStoreFilter(s.code)}
            className={`text-left bg-white border rounded-lg p-3 hover:border-blue-300 transition ${storeFilter === s.code ? "border-blue-600 ring-1 ring-blue-100" : "border-slate-200"}`}
            data-testid={`store-tile-${s.code}`}
          >
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{s.name}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <div className="text-xl font-extrabold text-slate-900 mono">{s.count}</div>
              {s.low > 0 && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-50 text-red-700">{s.low} low</span>
              )}
            </div>
            <div className="text-[11px] text-slate-500">SKU lines</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="inv-search-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code or name…"
            className="pl-8 w-72 h-9"
            data-testid="inv-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700"
              data-testid="inv-search-clear"
            >
              Clear
            </button>
          )}
        </div>
        <span className="text-xs text-slate-500 mono">{sorted.length} line(s)</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="inventory-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-3 px-4">Part Number</th><th className="py-3 px-4">Description</th>
              <th className="py-3 px-4">Type</th><th className="py-3 px-4">Warehouse</th>
              <th className="py-3 px-4">On Hand</th><th className="py-3 px-4">Reorder Point</th>
              <th className="py-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const item = itemMap[r.item_id];
              const low = r.qty <= r.reorder_point;
              return (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`inv-row-${item?.code}`}>
                  <td className="py-3 px-4 mono font-medium">{item?.code}</td>
                  <td className="py-3 px-4 text-slate-700">{item?.name}</td>
                  <td className="py-3 px-4 text-xs uppercase text-slate-500 tracking-wider">{TYPE_DISPLAY_LABELS[item?.type] || item?.type}</td>
                  <td className="py-3 px-4 text-slate-600 text-xs">{storeMap[r.warehouse] || r.warehouse}</td>
                  <td className={`py-3 px-4 mono font-semibold ${low ? "text-red-600" : "text-slate-800"}`}>{r.qty}</td>
                  <td className="py-3 px-4 mono text-slate-500">{r.reorder_point}</td>
                  <td className="py-3 px-4">
                    {low ? (
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                        <AlertTriangle className="w-3 h-3" /> Reorder
                      </span>
                    ) : (
                      <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">OK</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-slate-500 text-sm">
                  {search ? <>No SKUs match <span className="mono text-slate-700">"{search}"</span>.</> : "No inventory records."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!importSummary} onOpenChange={(o) => { if (!o) setImportSummary(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="inv-import-summary">
          <DialogHeader>
            <DialogTitle>Stock import summary</DialogTitle>
          </DialogHeader>
          {importSummary && (
            <div className="space-y-5 text-sm">
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-blue-900" data-testid="inv-import-stock-count">
                <span className="font-semibold">Physical stock update:</span> On Hand for every part/store in{" "}
                <span className="mono">{importSummary.file}</span> was <b>replaced</b> with the file's quantity.
                Parts/stores not in the file were not changed{importSummary.unchanged ? `; ${importSummary.unchanged} line(s) already matched` : ""}.
                Fabrication and Powder Coating stock is never changed by import. Each change is in the Stock Ledger as
                "Physical stock update — [part number]" (note: Replaced via import).
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Parts updated</div>
                  <div className="text-2xl font-bold text-slate-900" data-testid="inv-import-updated">{importSummary.updated}</div>
                  <div className="text-xs text-slate-500">On Hand replaced</div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Parts created</div>
                  <div className="text-2xl font-bold text-emerald-700" data-testid="inv-import-created">{importSummary.created}</div>
                  <div className="text-xs text-slate-500">new part / store</div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Errors</div>
                  <div className={`text-2xl font-bold ${importSummary.errors?.length ? "text-red-600" : "text-slate-900"}`} data-testid="inv-import-errors">{importSummary.errors?.length || 0}</div>
                  <div className="text-xs text-slate-500">rows skipped</div>
                </div>
              </div>
              {[["Parts updated (On Hand replaced)", importSummary.updated_parts], ["Parts created (new part / store)", importSummary.created_parts]].map(([title, list]) => (
                list?.length ? (
                  <div key={title}>
                    <div className="font-semibold text-slate-800 mb-1.5">{title}</div>
                    <div className="border border-slate-200 rounded-md overflow-x-auto max-h-64">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500 text-left">
                          <tr>
                            <th className="py-1.5 px-3">Part Number</th>
                            <th className="py-1.5 px-3">Store</th>
                            <th className="py-1.5 px-3 text-right">Previous</th>
                            <th className="py-1.5 px-3 text-right">Change</th>
                            <th className="py-1.5 px-3 text-right">New On Hand</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((r) => (
                            <tr key={`${r.part_number}|${r.warehouse}`} className="border-t border-slate-100">
                              <td className="py-1.5 px-3 mono">{r.part_number}</td>
                              <td className="py-1.5 px-3">{r.store}</td>
                              <td className="py-1.5 px-3 text-right mono">{r.previous_qty}</td>
                              <td className={`py-1.5 px-3 text-right mono ${r.qty_added < 0 ? "text-red-600" : "text-emerald-700"}`}>
                                {r.qty_added >= 0 ? "+" : ""}{r.qty_added}
                              </td>
                              <td className="py-1.5 px-3 text-right mono font-semibold">{r.new_qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null
              ))}
              {importSummary.skipped?.length > 0 && (
                <div>
                  <div className="font-semibold text-slate-700 mb-1.5">Not changed — Fabrication / Powder Coating ({importSummary.skipped.length})</div>
                  <ul className="border border-slate-200 bg-slate-50 rounded-md p-3 space-y-1 text-xs text-slate-600 max-h-40 overflow-y-auto" data-testid="inv-import-skipped">
                    {importSummary.skipped.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              {importSummary.errors?.length > 0 && (
                <div>
                  <div className="font-semibold text-red-700 mb-1.5">Errors</div>
                  <ul className="border border-red-200 bg-red-50 rounded-md p-3 space-y-1 text-xs text-red-800 max-h-48 overflow-y-auto">
                    {importSummary.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportSummary(null)} data-testid="inv-import-summary-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
